import { useState, useRef, useCallback } from 'react'
import { PlusCircle, StopCircle, ArrowRightLeft, Type, Minus } from 'lucide-react'

interface Props {
  onAddActivity: () => void
  onAddEnd: () => void
  onToggleTransition: () => void
  transitionMode: boolean
  theme: 'dark' | 'light'
}

export default function CanvasToolbar({
  onAddActivity,
  onAddEnd,
  onToggleTransition,
  transitionMode,
  theme,
}: Props) {
  const [showText, setShowText] = useState(true)
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null)
  const draggingRef = useRef(false)
  const dragStartRef = useRef({ x: 0, y: 0 })
  const posStartRef = useRef({ x: 0, y: 0 })
  const toolbarRef = useRef<HTMLDivElement>(null)
  const didDragRef = useRef(false)

  const bg = theme === 'light'
    ? 'rgba(255, 255, 255, 0.95)'
    : 'rgba(30, 30, 30, 0.95)'

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only start drag on the toolbar background, not on buttons
    if ((e.target as HTMLElement).closest('button')) return
    e.preventDefault()
    draggingRef.current = true
    didDragRef.current = false
    dragStartRef.current = { x: e.clientX, y: e.clientY }
    const toolbar = toolbarRef.current
    if (toolbar) {
      const rect = toolbar.getBoundingClientRect()
      posStartRef.current = { x: rect.left, y: rect.top }
    }

    const onMove = (ev: MouseEvent) => {
      if (!draggingRef.current) return
      const dx = ev.clientX - dragStartRef.current.x
      const dy = ev.clientY - dragStartRef.current.y
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDragRef.current = true
      setPosition({
        x: posStartRef.current.x + dx,
        y: posStartRef.current.y + dy,
      })
    }
    const onUp = () => {
      draggingRef.current = false
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'grabbing'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [])

  const posStyle: React.CSSProperties = position
    ? {
        position: 'fixed',
        left: position.x,
        top: position.y,
        transform: 'none',
      }
    : {
        position: 'absolute',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
      }

  return (
    <div
      ref={toolbarRef}
      onMouseDown={handleMouseDown}
      style={{
        ...posStyle,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        background: bg,
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        padding: '4px 6px',
        zIndex: 10,
        backdropFilter: 'blur(8px)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        cursor: draggingRef.current ? 'grabbing' : 'grab',
      }}
    >
      <ToolBtn
        icon={<PlusCircle size={16} />}
        label="Activity"
        showText={showText}
        tooltip="Add Activity"
        onClick={onAddActivity}
      />
      <ToolBtn
        icon={<StopCircle size={16} />}
        label="End"
        showText={showText}
        tooltip="Add End"
        onClick={onAddEnd}
      />
      <div style={{
        width: 1,
        height: 20,
        background: 'var(--color-border)',
        margin: '0 2px',
      }} />
      <ToolBtn
        icon={<ArrowRightLeft size={16} />}
        label="Connect"
        showText={showText}
        tooltip={transitionMode ? 'Cancel Transition' : 'Connect Activities'}
        onClick={onToggleTransition}
        active={transitionMode}
      />
      <div style={{
        width: 1,
        height: 20,
        background: 'var(--color-border)',
        margin: '0 2px',
      }} />
      <button
        onClick={() => setShowText(prev => !prev)}
        title={showText ? 'Hide labels' : 'Show labels'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: 4,
          border: 'none',
          cursor: 'pointer',
          color: 'var(--color-text-secondary)',
          background: 'transparent',
          transition: 'background 0.15s',
          padding: 0,
        }}
        onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
      >
        {showText ? <Minus size={12} /> : <Type size={12} />}
      </button>
    </div>
  )
}

function ToolBtn({
  icon,
  label,
  showText,
  tooltip,
  onClick,
  active,
}: {
  icon: React.ReactNode
  label: string
  showText: boolean
  tooltip: string
  onClick: () => void
  active?: boolean
}) {
  return (
    <button
      onClick={onClick}
      title={tooltip}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: showText ? 5 : 0,
        height: 30,
        padding: showText ? '0 10px' : '0 7px',
        borderRadius: 6,
        border: 'none',
        cursor: 'pointer',
        color: active ? 'var(--color-accent)' : 'var(--color-text)',
        background: active ? 'var(--color-accent-weak)' : 'transparent',
        transition: 'background 0.15s, color 0.15s',
        fontSize: '0.8125rem',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => {
        if (!active) e.currentTarget.style.background = 'var(--color-bg-secondary)'
      }}
      onMouseLeave={e => {
        if (!active) e.currentTarget.style.background = 'transparent'
      }}
    >
      {icon}
      {showText && <span>{label}</span>}
    </button>
  )
}
